import React, { useEffect, useState, useRef } from "react";
import { SignalPulse } from "../components/SignalPulse";
import { useParams, useNavigate } from "react-router-dom";
import { Send, Loader2, ArrowLeft, Lock, Flag, Trash2, Check, ImagePlus, X, PenSquare, Search } from "lucide-react";
import api from "../lib/api";
import { Avatar, SecureMedia } from "../components/Media";
import { StreamRefCard } from "../components/StreamShare";
import { CategoryPill } from "./Feed";
import { RichText } from "../components/RichText";
import { DisturbanceModal } from "../components/DisturbanceModal";
import { NewExchangeDialog } from "../components/NewExchangeDialog";
import { matchesMember } from "../lib/memberSearch";
import { useCrypto } from "../context/CryptoContext";
import { useRealtime } from "../context/RealtimeContext";

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
};

const dayLabel = (iso) => {
  try {
    const d = new Date(iso), now = new Date();
    const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const diff = Math.round((startOf(now) - startOf(d)) / 86400000);
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
};
const sameDay = (a, b) => { try { return new Date(a).toDateString() === new Date(b).toDateString(); } catch { return false; } };

export default function Messages() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { encryptDm, decryptDm, encryptDmBytes } = useCrypto();
  const { event, send: wsSend } = useRealtime();
  const [conversations, setConversations] = useState(null);
  const [messages, setMessages] = useState([]);
  const [texts, setTexts] = useState({});
  const [other, setOther] = useState(null);
  const [text, setText] = useState(() => new URLSearchParams(window.location.search).get("draft") || "");
  useEffect(() => { if (new URLSearchParams(window.location.search).get("draft")) window.history.replaceState({}, "", window.location.pathname); }, []);
  const [peerTyping, setPeerTyping] = useState(false);
  const typingSentRef = useRef(0);
  const typingClearRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const autoGrow = (el) => { if (!el) return; el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 140) + "px"; };
  useEffect(() => { if (text) autoGrow(inputRef.current); }, [other]); // eslint-disable-line react-hooks/exhaustive-deps
  const [flagTarget, setFlagTarget] = useState(null);
  const [sending, setSending] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [convoQ, setConvoQ] = useState("");
  const sendingRef = useRef(false);
  const endRef = useRef();
  const scrollRef = useRef(null);
  const innerRef = useRef(null);
  const stickRef = useRef(true);

  const SharedPostPreview = ({ postId, mine }) => {
    const [p, setP] = useState(undefined);
    useEffect(() => {
      api.get(`/posts/${postId}`).then(({ data }) => setP(data)).catch(() => setP(null));
    }, [postId]);
    if (p === undefined) return <div className="w-48 h-32 bg-black/20 flex items-center justify-center"><Loader2 className="animate-spin text-zinc-400" size={16} /></div>;
    if (p === null) return <div className="font-body text-xs italic opacity-70 py-1">Post unavailable</div>;
    return (
      <button onClick={() => navigate(`/post/${postId}`)} data-testid="shared-post-preview" className={`block w-48 overflow-hidden border ${mine ? "border-black/10" : "border-white/10"} bg-black/30 text-left`}>
        <SecureMedia path={p.media_path} iv={p.media_iv} enc={p.enc} type={p.media_type} className="w-full h-28" />
        <div className="p-2 space-y-1">
          <div className={`font-heading text-xs truncate ${mine ? "text-[#0A0A0A]" : "text-white"}`}>{p.author.name}</div>
          <CategoryPill cat={p.category} />
        </div>
      </button>
    );
  };

  const loadConvos = async () => {
    const { data } = await api.get("/conversations");
    setConversations(data);
  };

  const loadThread = async () => {
    if (!id) return;
    const [{ data: msgs }, { data: prof }] = await Promise.all([
      api.get(`/messages/${id}`),
      api.get(`/members/${id}`),
    ]);
    setMessages((prev) => {
      const pending = prev.filter((m) => m._pending);
      const next = pending.length ? [...msgs, ...pending] : msgs;
      if (prev.length === next.length && prev.every((m, i) => m.id === next[i].id && m.read === next[i].read)) return prev;
      return next;
    });
    setOther(prof);
  };

  // decrypt any messages we haven't decrypted yet
  useEffect(() => {
    (async () => {
      const next = {};
      let changed = false;
      for (const m of messages) {
        if (texts[m.id] !== undefined) { next[m.id] = texts[m.id]; continue; }
        if (m.enc && m.ct) {
          const pt = await decryptDm(id, m);
          next[m.id] = pt === null ? "__LOCKED__" : pt;
        } else if (m.enc) {
          next[m.id] = ""; // media-only message, no text to decrypt
        } else {
          next[m.id] = m.text;
        }
        changed = true;
      }
      if (changed) setTexts(next);
    })();
    /* eslint-disable-next-line */
  }, [messages, id]);

  useEffect(() => { loadConvos(); const t = setInterval(loadConvos, 30000); return () => clearInterval(t); }, []);
  useEffect(() => {
    setMessages([]); setOther(null); setPeerTyping(false); // clear stale thread when switching conversations
    loadThread();
    if (!id) return;
    const t = setInterval(loadThread, 30000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [id]);
  // instant update on realtime message
  useEffect(() => {
    if (!event) return;
    if (event.type === "message") { loadConvos(); loadThread(); }
    if (event.type === "typing" && event.from_user_id === id) {
      if (event.state === "stop") { setPeerTyping(false); }
      else {
        setPeerTyping(true);
        clearTimeout(typingClearRef.current);
        typingClearRef.current = setTimeout(() => setPeerTyping(false), 4000);
      }
    }
    /* eslint-disable-next-line */
  }, [event]);
  // Open every conversation at the newest message, and stay there while photos load in,
  // unless the member has scrolled up to read older messages.
  const toBottom = () => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; };
  useEffect(() => { stickRef.current = true; }, [id]);
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last && last.mine && last._pending) stickRef.current = true;
    if (stickRef.current) toBottom();
  }, [messages, peerTyping, texts]);
  useEffect(() => {
    const inner = innerRef.current;
    if (!inner || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => { if (stickRef.current) toBottom(); });
    ro.observe(inner);
    return () => ro.disconnect();
  }, [id, other]);

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || sendingRef.current) return;   // hard guard against double-send
    sendingRef.current = true;
    setSending(true);
    // Instant feedback: clear the box and show an optimistic "sending" bubble now,
    // so the delay from encryption + network never tempts a second press.
    setText("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    if (id) wsSend({ type: "typing", to: id, state: "stop" });
    typingSentRef.current = 0;
    const tempId = `temp-${Date.now()}`;
    setTexts((t) => ({ ...t, [tempId]: body }));
    setMessages((ms) => [...ms, { id: tempId, mine: true, enc: true, ct: "pending", created_at: new Date().toISOString(), _pending: true }]);
    try {
      const payload = await encryptDm(id, body);
      const { data } = await api.post("/messages", { to_user_id: id, ...payload });
      setTexts((t) => { const n = { ...t }; delete n[tempId]; n[data.id] = body; return n; });
      setMessages((ms) => ms.map((m) => (m.id === tempId ? data : m)));
      loadConvos();
    } catch (err) {
      setMessages((ms) => ms.filter((m) => m.id !== tempId));
      setTexts((t) => { const n = { ...t }; delete n[tempId]; return n; });
      setText(body);
      window.alert("Couldn't send that message. Please try again.");
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const compressImage = (file) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const max = 1600;
      let { width, height } = img;
      if (width > max || height > max) { const r = Math.min(max / width, max / height); width = Math.round(width * r); height = Math.round(height * r); }
      const c = document.createElement("canvas");
      c.width = width; c.height = height;
      c.getContext("2d").drawImage(img, 0, 0, width, height);
      c.toBlob((b) => resolve(b || file), "image/jpeg", 0.85);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });

  const uploadEncrypted = async (blob, filename) => {
    const CHUNK = 5 * 1024 * 1024;
    if (blob.size <= CHUNK) {
      const fd = new FormData();
      fd.append("file", blob, filename); fd.append("enc", "true");
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => { if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100)); },
      });
      return data.path;
    }
    const uploadId = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now());
    const total = Math.ceil(blob.size / CHUNK);
    for (let i = 0; i < total; i++) {
      const part = blob.slice(i * CHUNK, (i + 1) * CHUNK);
      const fd = new FormData();
      fd.append("upload_id", uploadId); fd.append("index", String(i)); fd.append("chunk", part, "chunk");
      await api.post("/upload/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadPct(Math.round(((i + 1) / total) * 100));
    }
    const { data } = await api.post("/upload/complete", { upload_id: uploadId, filename, content_type: "application/octet-stream", enc: true });
    return data.path;
  };

  const onPickMedia = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file || !id) return;
    const isVideo = file.type.startsWith("video");
    const isImage = file.type.startsWith("image");
    if (!isVideo && !isImage) return;
    setUploading(true); setUploadPct(0);
    try {
      const blob = isImage ? await compressImage(file) : file;
      const buf = await blob.arrayBuffer();
      const enc = await encryptDmBytes(id, buf);
      if (!enc) throw new Error("encrypt failed");
      const path = await uploadEncrypted(new Blob([enc.ctBuf]), isVideo ? "enc.mp4.bin" : "enc.bin");
      const { data } = await api.post("/messages", {
        to_user_id: id, enc: true, media_path: path, media_iv: enc.ivB64, media_type: isVideo ? "video" : "image",
      });
      setTexts((t) => ({ ...t, [data.id]: "" }));
      setMessages((ms) => [...ms, data]);
      loadConvos();
    } catch (err) {
      window.alert("Couldn't send that media. Please try a smaller file or try again.");
    }
    setUploading(false);
  };

  const onType = (e) => {
    setText(e.target.value); autoGrow(e.target);
    if (!id) return;
    const now = Date.now();
    if (now - typingSentRef.current > 2500) {
      typingSentRef.current = now;
      wsSend({ type: "typing", to: id, state: "start" });
    }
  };

  const deleteMsg = async (mid) => {
    if (!window.confirm("Delete this message? This removes it for everyone.")) return;
    setMessages((prev) => prev.filter((x) => x.id !== mid));
    try { await api.delete(`/messages/${mid}`); } catch { loadThread(); }
    loadConvos();
  };

  return (
    <div className="max-w-6xl mx-auto h-[calc(100vh-3.5rem-4rem)] md:h-screen -mb-20 md:mb-0 flex">
      {flagTarget && <DisturbanceModal target={flagTarget} onClose={() => setFlagTarget(null)} />}
      {/* Conversation list */}
      <div className={`${id ? "hidden md:flex" : "flex"} flex-col w-full md:w-80 border-r border-white/10`}>
        <div className="px-6 py-6 border-b border-white/10 flex items-center justify-between gap-3">
          <h1 className="font-heading text-2xl font-medium tracking-tight text-[#14A89A]">Exchange</h1>
          <button type="button" onClick={() => setNewOpen(true)} data-testid="new-exchange-btn" title="Start a new exchange"
            className="flex items-center gap-1.5 border border-[#14A89A]/60 text-[#14A89A] hover:bg-[#14A89A] hover:text-[#0A0A0A] px-3 py-1.5 font-body text-xs transition-colors">
            <PenSquare size={14} /> New
          </button>
        </div>
        <NewExchangeDialog open={newOpen} onOpenChange={setNewOpen} onPick={(uid) => navigate(`/messages/${uid}`)} />
        {conversations?.length > 0 && (
          <div className="px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2 border border-white/15 px-3 focus-within:border-[#14A89A] transition-colors">
              <Search size={14} className="text-zinc-500 shrink-0" />
              <input value={convoQ} onChange={(e) => setConvoQ(e.target.value)} placeholder="Search exchanges" data-testid="exchange-search-input"
                className="flex-1 min-w-0 bg-transparent py-2 font-body text-sm text-white outline-none placeholder:text-zinc-600" />
              {convoQ && <button type="button" onClick={() => setConvoQ("")} aria-label="Clear search" data-testid="exchange-search-clear" className="text-zinc-500 hover:text-white"><X size={14} /></button>}
            </div>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {conversations === null ? (
            <div className="flex justify-center py-10"><SignalPulse size={38} /></div>
          ) : conversations.length === 0 ? (
            <p className="font-body text-sm text-zinc-600 p-6">No exchanges yet. Tap New to start one with any sovereign.</p>
          ) : convoQ && !conversations.some((c) => matchesMember(c.user, convoQ)) ? (
            <p className="font-body text-sm text-zinc-600 p-6" data-testid="exchange-search-empty">No exchanges match "{convoQ}". <button type="button" onClick={() => setNewOpen(true)} className="text-[#14A89A] hover:underline">Start a new one</button></p>
          ) : conversations.filter((c) => matchesMember(c.user, convoQ)).map((c) => (
            <button key={c.user.id} onClick={() => navigate(`/messages/${c.user.id}`)} data-testid="conversation-item" className={`w-full flex items-center gap-3 px-6 py-4 hover:bg-white/5 transition-colors text-left ${id === c.user.id ? "bg-white/5" : ""}`}>
              <Avatar name={c.user.name} src={c.user.avatar} size={44} />
              <div className="min-w-0 flex-1">
                <div className="font-heading text-sm text-white truncate">{c.user.name}</div>
                <div className="font-body text-xs text-zinc-500 truncate flex items-center gap-1">
                  {c.last_enc ? <><Lock size={10} /> Encrypted message</> : c.last_message}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Thread */}
      <div className={`${id ? "flex" : "hidden md:flex"} flex-col flex-1`}>
        {!id ? (
          <div className="flex-1 flex items-center justify-center font-body text-sm text-zinc-600">Select an exchange</div>
        ) : (
          <>
            <div className="flex items-center gap-3 px-4 md:px-6 py-4 border-b border-white/10">
              <button onClick={() => navigate("/messages")} className="md:hidden text-zinc-400"><ArrowLeft size={20} /></button>
              {other && <><Avatar name={other.name} src={other.avatar} size={40} /><div><div className="font-heading text-sm text-white">{other.name}</div><div className="font-body text-xs text-zinc-500">{other.headline}</div></div></>}
            </div>
            <div ref={scrollRef} onScroll={(e) => { const el = e.currentTarget; stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 150; }} className="flex-1 overflow-y-auto p-4 md:p-6" data-testid="dm-thread-scroll">
             <div ref={innerRef} className="space-y-3">
              {messages.map((m, idx) => {
                const display = texts[m.id];
                const locked = display === "__LOCKED__" || (m.enc && display === undefined);
                const showDay = idx === 0 || !sameDay(messages[idx - 1].created_at, m.created_at);
                return (
                  <React.Fragment key={m.id}>
                    {showDay && (
                      <div className="flex items-center justify-center py-2" data-testid="day-divider">
                        <span className="font-body text-[10px] tracking-[0.15em] uppercase text-zinc-500 bg-white/5 px-3 py-1 rounded-full">{dayLabel(m.created_at)}</span>
                      </div>
                    )}
                    <div className={`flex items-center gap-2 group ${m.mine ? "justify-end" : "justify-start"}`} data-testid="message-bubble">
                      {m.mine && (
                        <button onClick={() => deleteMsg(m.id)} data-testid="dm-delete-button" title="Delete this message" className="shrink-0 text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 size={13} />
                        </button>
                      )}
                      <div className={`flex flex-col max-w-[75%] ${m.mine ? "items-end" : "items-start"}`}>
                        <div className={`px-4 py-2.5 font-body text-sm ${m.mine ? "bg-[#14A89A] text-[#0A0A0A]" : "bg-[#1a1a1a] text-zinc-200 border border-white/10"}`}>
                          {m.post_ref && <div className="mb-2"><SharedPostPreview postId={m.post_ref} mine={m.mine} /></div>}
                          {m.stream_ref && <div className="mb-2 w-56 max-w-full"><StreamRefCard streamRef={m.stream_ref} compact /></div>}
                          {m.media_path && (
                            <div className={`${(m.ct || m.text) ? "mb-2" : ""} -mx-1 -mt-0.5`}>
                              {locked ? (
                                <span className="flex items-center gap-1 italic opacity-70"><Lock size={12} /> Encrypted media</span>
                              ) : (
                                <div className="w-56 max-w-full overflow-hidden rounded">
                                  <SecureMedia path={m.media_path} iv={m.media_iv} enc type={m.media_type} dmWith={id} onExpand={(url) => setLightbox({ path: m.media_path, iv: m.media_iv, type: m.media_type, url })} />
                                </div>
                              )}
                            </div>
                          )}
                          {(m.ct || m.text) && (locked ? <span className="flex items-center gap-1 italic opacity-70"><Lock size={12} /> Encrypted</span> : <span className="whitespace-pre-wrap break-words"><RichText text={display} linkClassName={`underline decoration-1 underline-offset-2 break-all ${m.mine ? "text-[#0A0A0A] font-semibold hover:opacity-80" : "text-[#14A89A] hover:text-[#2BC4B5]"}`} /></span>)}
                        </div>
                        <span className="font-body text-[10px] text-zinc-600 mt-1 flex items-center gap-1" data-testid="message-time">
                          {fmtDateTime(m.created_at)}
                          {m.mine && (m._pending
                            ? <span className="flex items-center gap-0.5 opacity-70" data-testid="msg-sending" title="Sending"><Loader2 size={11} className="animate-spin" /> Sending…</span>
                            : <span className="flex items-center gap-0.5" data-testid="msg-sent" title="Sent"><Check size={12} /> Sent</span>)}
                        </span>
                      </div>
                      {!m.mine && (
                        <button onClick={() => setFlagTarget({ type: "dm", id: m.id, authorId: id, snippet: (display && display !== "__LOCKED__") ? display : "(encrypted message)" })} data-testid="dm-flag-button" title="Report a disturbance" className="shrink-0 text-zinc-600 hover:text-[#A855F7] opacity-0 group-hover:opacity-100 transition-opacity">
                          <Flag size={13} />
                        </button>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
              {peerTyping && (
                <div className="flex justify-start" data-testid="typing-indicator">
                  <div className="bg-[#1a1a1a] border border-white/10 px-4 py-3 flex items-center gap-1.5">
                    <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                  </div>
                </div>
              )}
              <div ref={endRef} />
             </div>
            </div>
            <form onSubmit={send} className="flex flex-col gap-2 p-4 md:pb-16 border-t border-white/10">
              <div className="flex items-center justify-center gap-1.5 font-body text-[10px] text-zinc-600" data-testid="dm-e2e-note"><Lock size={9} className="text-[#14A89A]" /> Encrypted end-to-end: only you two can read this</div>
              <div className="flex items-end gap-3">
                <input ref={fileRef} type="file" accept="image/*,video/*" onChange={onPickMedia} className="hidden" data-testid="dm-media-input" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="dm-attach-button" title="Send a photo or video" className="relative text-[#D4AF37] hover:text-[#F3E5AB] p-3 shrink-0 disabled:opacity-50 transition-colors">
                  {uploading ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={20} strokeWidth={1.5} />}
                  {uploading && uploadPct > 0 && <span data-testid="dm-upload-pct" className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9px] font-bold text-[#14A89A]">{uploadPct}%</span>}
                </button>
                <textarea
                  ref={inputRef}
                  value={text}
                  onChange={onType}
                  placeholder="Transmit privately..."
                  rows={1}
                  data-testid="message-input"
                  className="flex-1 resize-none max-h-[140px] overflow-y-auto bg-transparent border border-white/15 px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body leading-snug"
                />
                <button type="submit" disabled={sending || !text.trim()} data-testid="send-message-button" className="bg-[#14A89A] text-[#0A0A0A] p-3 hover:bg-[#2BC4B5] transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed">{sending ? <SignalPulse size={16} inline /> : <Send size={18} />}</button>
              </div>
            </form>
          </>
        )}
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" data-testid="dm-media-lightbox" style={{ touchAction: "none" }} onClick={() => setLightbox(null)}>
          <button onClick={(e) => { e.stopPropagation(); setLightbox(null); }} data-testid="dm-lightbox-close" className="absolute top-4 right-4 z-20 text-white/80 hover:text-white bg-black/40 rounded-full p-2 transition-colors"><X size={22} /></button>
          <SecureMedia path={lightbox.path} iv={lightbox.iv} enc type={lightbox.type} dmWith={id} preUrl={lightbox.url} fullscreen onZoomChange={() => {}} />
        </div>
      )}
    </div>
  );
}
